/**
 * Finance Page Component
 * Dashboard, payout queue, withdrawals, Share & Earn, and placeholder views
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../store/AdminContext';
import { apiService } from '../services/apiService';
import ConfirmModal from '../components/modals/ConfirmModal';
import WithdrawalReviewPanel from '../components/modals/WithdrawalReviewPanel';
import { CampaignReleaseWizard } from '../components/CampaignReleaseWizard';
import './FinancePage.css';

interface ReferralCampaign {
  id: string;
  key: string;
  enabled: boolean;
  config: Record<string, unknown>;
  version: number;
  isDefault: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CreativeTemplateSummary {
  id: string;
  key: string;
  enabled: boolean;
  channel: string;
  config: Record<string, unknown>;
  version: number;
  publishedAt: string | null;
}

interface ScoreModelSummary {
  id: string;
  key: string;
  enabled: boolean;
  version: number;
  config: Record<string, unknown>;
  publishedAt: string | null;
}

interface CountryPolicySummary {
  id: string;
  countryCode: string;
  policyKey: string;
  config: Record<string, unknown>;
  effectiveTo: string | null;
}

interface ReferralAbuseEvent {
  id: string;
  referrerEmail: string;
  referredEmail: string;
  suspicionReason: string;
  reviewStatus?: string | null;
  reviewReason?: string | null;
}

interface ReferralPayoutRow {
  id: string;
  recipientRole: string;
  amountCents: number;
  status: string;
  createdAt: string;
  recipientEmail: string;
  campaignKey: string | null;
}

interface ReferralFunnelSummary {
  clicks: number;
  attributions: number;
  conversions: number;
  payoutCents: number;
}

interface ReferralCampaignAnalyticsRow {
  id: string;
  key: string;
  version: number;
  publishedAt: string | null;
  links: number;
  clicks: number;
  attributions: number;
  payoutCents: number;
}

const ShareEarnView: React.FC = () => {
  const [campaigns, setCampaigns] = useState<ReferralCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [releaseWizardOpen, setReleaseWizardOpen] = useState(false);
  const [formKey, setFormKey] = useState('');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formConfig, setFormConfig] = useState<string>('{}');
  const [templates, setTemplates] = useState<CreativeTemplateSummary[]>([]);
  const [scoreModels, setScoreModels] = useState<ScoreModelSummary[]>([]);
  const [countryPolicies, setCountryPolicies] = useState<CountryPolicySummary[]>([]);
  const [abuseEvents, setAbuseEvents] = useState<ReferralAbuseEvent[]>([]);
  const [recentPayouts, setRecentPayouts] = useState<ReferralPayoutRow[]>([]);
  const [funnel, setFunnel] = useState<ReferralFunnelSummary | null>(null);
  const [campaignStats, setCampaignStats] = useState<ReferralCampaignAnalyticsRow[]>([]);
  const [payoutActioningId, setPayoutActioningId] = useState<string | null>(null);
  const [tagUserId, setTagUserId] = useState('');
  const [tagBrand, setTagBrand] = useState('');
  const [tagCohort, setTagCohort] = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const [diagReferredUserId, setDiagReferredUserId] = useState('');
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagResult, setDiagResult] = useState<Awaited<ReturnType<typeof apiService.getReferralDiagnostics>> | null>(null);

  const loadCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const { campaigns: data } = await apiService.getReferralCampaigns();
      setCampaigns(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const { templates: data } = await apiService.getCreativeTemplatesAdmin();
      setTemplates(data || []);
    } catch {
      setTemplates([]);
    }
  };

  const loadScoreModels = async () => {
    try {
      const { models } = await apiService.getScoreModels();
      setScoreModels((models || []) as ScoreModelSummary[]);
    } catch {
      setScoreModels([]);
    }
  };

  const loadCountryPolicies = async () => {
    try {
      const { overrides } = await apiService.getCountryPolicies();
      setCountryPolicies((overrides || []) as CountryPolicySummary[]);
    } catch {
      setCountryPolicies([]);
    }
  };

  const loadAbuseReview = async () => {
    try {
      const { events } = await apiService.getReferralAbuseReview();
      setAbuseEvents((events || []) as ReferralAbuseEvent[]);
    } catch {
      setAbuseEvents([]);
    }
  };

  const loadPayouts = async () => {
    try {
      const data = await apiService.getReferralAnalytics();
      setRecentPayouts((data.recentPayouts || []) as ReferralPayoutRow[]);
      setFunnel(data.summary ?? null);
      setCampaignStats((data.campaigns || []) as ReferralCampaignAnalyticsRow[]);
    } catch {
      setRecentPayouts([]);
      setFunnel(null);
      setCampaignStats([]);
    }
  };

  useEffect(() => {
    loadCampaigns();
    loadTemplates();
    loadScoreModels();
    loadCountryPolicies();
    loadAbuseReview();
    loadPayouts();
  }, []);

  const handleReversePayout = async (id: string) => {
    setError(null);
    setPayoutActioningId(id);
    try {
      await apiService.reverseReferralPayout(id);
      setSuccess('Payout reversed');
      loadPayouts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reverse payout');
    } finally {
      setPayoutActioningId(null);
    }
  };

  const handleBlockPayout = async (id: string) => {
    setError(null);
    setPayoutActioningId(id);
    try {
      await apiService.blockReferralPayout(id);
      setSuccess('Payout blocked');
      loadPayouts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to block payout');
    } finally {
      setPayoutActioningId(null);
    }
  };

  const parseConfig = (): Record<string, unknown> => {
    try {
      return JSON.parse(formConfig || '{}');
    } catch {
      return {};
    }
  };

  const handleCreate = async () => {
    if (!formKey.trim()) {
      setError('Key is required');
      return;
    }
    setError(null);
    try {
      await apiService.createReferralCampaign({
        key: formKey.trim(),
        enabled: formEnabled,
        config: parseConfig(),
      });
      setSuccess('Campaign created');
      setCreateOpen(false);
      setFormKey('');
      setFormConfig('{}');
      loadCampaigns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    }
  };

  const handleUpdate = async (id: string) => {
    setError(null);
    try {
      await apiService.updateReferralCampaign(id, {
        enabled: formEnabled,
        config: parseConfig(),
      });
      setSuccess('Campaign updated');
      setEditingId(null);
      loadCampaigns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update campaign');
    }
  };

  const handlePublish = async (id: string) => {
    setError(null);
    try {
      await apiService.publishReferralCampaign(id);
      setSuccess('Campaign published');
      loadCampaigns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish campaign');
    }
  };

  const handleValidate = async (config: Record<string, unknown>) => {
    setError(null);
    try {
      const result = await apiService.validateReferralCampaign(config);
      if (!result.valid) {
        setError(result.errors.join(', '));
        return;
      }
      setSuccess('Campaign config is valid');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to validate campaign');
    }
  };

  const handlePreview = async (config: Record<string, unknown>) => {
    setError(null);
    try {
      const result = await apiService.previewReferralCampaign(config, {
        country: 'GB',
        stakeCents: 1000,
        oddsInt: 18000,
      });
      setSuccess(
        `Preview payout: referrer £${(result.referrerCents / 100).toFixed(2)}, referee £${(result.refereeCents / 100).toFixed(2)}`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview campaign');
    }
  };

  const handleSetDefault = async (id: string) => {
    setError(null);
    try {
      await apiService.setDefaultReferralCampaign(id);
      setSuccess('Default campaign updated');
      loadCampaigns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set default campaign');
    }
  };

  const handleVersions = async (id: string) => {
    setError(null);
    try {
      const { versions } = await apiService.getReferralCampaignVersions(id);
      const message = versions
        .slice(0, 8)
        .map((version) => {
          const published = version.publishedAt ? `published ${new Date(version.publishedAt).toLocaleString()}` : 'draft';
          return `v${version.version} · ${published}`;
        })
        .join('\n');
      window.alert(message || 'No versions available');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load versions');
    }
  };

  const handleRollback = async (id: string) => {
    const versionInput = window.prompt('Rollback to which version?');
    const version = Number(versionInput);
    if (!versionInput || !Number.isFinite(version) || version <= 0) return;
    setError(null);
    try {
      await apiService.rollbackReferralCampaign(id, version);
      setSuccess(`Campaign rolled back to v${version} and republished`);
      loadCampaigns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rollback campaign');
    }
  };

  const startEdit = (c: ReferralCampaign) => {
    setEditingId(c.id);
    setFormEnabled(c.enabled);
    setFormConfig(JSON.stringify(c.config, null, 2));
  };

  const cfg = (c: ReferralCampaign) => {
    const p = (c.config?.payout as Record<string, unknown>) ?? c.config;
    return {
      referrerCents: (c.config?.referrer_cents ?? p?.referrer_cents) as number | undefined,
      refereeCents: (c.config?.referee_cents ?? p?.referee_cents) as number | undefined,
      sharePercent: (p?.share_percent ?? c.config?.share_percent) as number | undefined,
      basis: (p?.basis as string) ?? 'fixed',
      maxBetsCount: (p?.max_bets_count ?? c.config?.max_bets_count ?? p?.max_predictions_count) as number | undefined,
    };
  };

  const handleCreateTemplate = async () => {
    const key = window.prompt('Template key');
    if (!key) return;
    const channel = (window.prompt('Channel (default, telegram, instagram_story, feed_card, web_og)', 'default') ||
      'default') as 'instagram_story' | 'telegram' | 'feed_card' | 'web_og' | 'default';
    const configInput = window.prompt('Template config JSON', '{"shareText":"Check out {{headline}} {{url}}"}');
    if (!configInput) return;
    try {
      await apiService.createCreativeTemplate({
        key,
        channel,
        enabled: true,
        config: JSON.parse(configInput),
      });
      setSuccess('Template created');
      loadTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create template');
    }
  };

  const handleEditTemplate = async (template: CreativeTemplateSummary) => {
    const configInput = window.prompt('Edit template config JSON', JSON.stringify(template.config, null, 2));
    if (!configInput) return;
    try {
      await apiService.updateCreativeTemplate(template.id, { config: JSON.parse(configInput) });
      setSuccess('Template updated');
      loadTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update template');
    }
  };

  const handlePublishTemplate = async (id: string) => {
    try {
      await apiService.publishCreativeTemplate(id);
      setSuccess('Template published');
      loadTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish template');
    }
  };

  const handleTemplateVersions = async (id: string) => {
    try {
      const { versions } = await apiService.getCreativeTemplateVersions(id);
      window.alert(
        versions
          .slice(0, 8)
          .map((version) => `v${version.version} · ${version.channel} · ${version.publishedAt ? 'published' : 'draft'}`)
          .join('\n') || 'No versions available'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template versions');
    }
  };

  const handleRollbackTemplate = async (id: string) => {
    const versionInput = window.prompt('Rollback template to which version?');
    const version = Number(versionInput);
    if (!versionInput || !Number.isFinite(version) || version <= 0) return;
    try {
      await apiService.rollbackCreativeTemplate(id, version);
      setSuccess(`Template rolled back to v${version}`);
      loadTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rollback template');
    }
  };

  const handleCreateScoreModel = async () => {
    const key = window.prompt('Score model key', 'prediction_skill_v1');
    const configInput = window.prompt(
      'Score model config JSON',
      '{"components":[{"metric":"user.metrics.settled_bet_count","weight":1}],"tiers":[{"min":0,"max":999999,"label":"Rookie"}]}'
    );
    if (!key || !configInput) return;
    try {
      await apiService.createScoreModel({ key, enabled: true, config: JSON.parse(configInput) });
      setSuccess('Score model created');
      loadScoreModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create score model');
    }
  };

  const handleEditScoreModel = async (model: ScoreModelSummary) => {
    const configInput = window.prompt('New score model config JSON', JSON.stringify(model.config, null, 2));
    if (!configInput) return;
    try {
      await apiService.updateScoreModel(model.id, { config: JSON.parse(configInput) });
      setSuccess('Score model updated');
      loadScoreModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update score model');
    }
  };

  const handlePublishScoreModel = async (id: string) => {
    try {
      await apiService.publishScoreModel(id);
      setSuccess('Score model published');
      loadScoreModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish score model');
    }
  };

  const handleScoreModelVersions = async (id: string) => {
    try {
      const { versions } = await apiService.getScoreModelVersions(id);
      window.alert(versions.slice(0, 8).map((version) => `v${version.version} · ${version.publishedAt ? 'published' : 'draft'}`).join('\n') || 'No versions available');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load score model versions');
    }
  };

  const handleRollbackScoreModel = async (id: string) => {
    const versionInput = window.prompt('Rollback score model to which version?');
    const version = Number(versionInput);
    if (!versionInput || !Number.isFinite(version) || version <= 0) return;
    try {
      await apiService.rollbackScoreModel(id, version);
      setSuccess(`Score model rolled back to v${version}`);
      loadScoreModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rollback score model');
    }
  };

  const handleCreateCountryPolicy = async () => {
    const countryCode = window.prompt('Country code', 'GB');
    const policyKey = window.prompt('Policy key', 'share_earn');
    const configInput = window.prompt('Country policy config JSON', '{"enabled":true}');
    if (!countryCode || !policyKey || !configInput) return;
    try {
      await apiService.upsertCountryPolicy({
        countryCode,
        policyKey,
        config: JSON.parse(configInput),
      });
      setSuccess('Country policy saved');
      loadCountryPolicies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save country policy');
    }
  };

  const handleEditCountryPolicy = async (policy: CountryPolicySummary) => {
    const configInput = window.prompt('Edit country policy config JSON', JSON.stringify(policy.config, null, 2));
    if (!configInput) return;
    try {
      await apiService.upsertCountryPolicy({
        countryCode: policy.countryCode,
        policyKey: policy.policyKey,
        config: JSON.parse(configInput),
        effectiveTo: policy.effectiveTo,
      });
      setSuccess('Country policy updated');
      loadCountryPolicies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update country policy');
    }
  };

  const handleCountryPolicyVersions = async (policy: CountryPolicySummary) => {
    try {
      const { versions } = await apiService.getCountryPolicyVersions(policy.countryCode, policy.policyKey);
      window.alert(versions.slice(0, 8).map((version) => `v${version.version} · ${version.createdAt}`).join('\n') || 'No versions available');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load country policy versions');
    }
  };

  const handleRollbackCountryPolicy = async (policy: CountryPolicySummary) => {
    const versionInput = window.prompt('Rollback country policy to which version?');
    const version = Number(versionInput);
    if (!versionInput || !Number.isFinite(version) || version <= 0) return;
    try {
      await apiService.rollbackCountryPolicy(policy.countryCode, policy.policyKey, version);
      setSuccess(`Country policy rolled back to v${version}`);
      loadCountryPolicies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rollback country policy');
    }
  };

  const loadShareEarnTags = async () => {
    const id = tagUserId.trim();
    if (!id) {
      setError('Enter a user UUID');
      return;
    }
    setError(null);
    setTagBusy(true);
    try {
      const t = await apiService.getUserShareEarnTags(id);
      setTagBrand(t.shareEarnBrand ?? '');
      setTagCohort(t.shareEarnCohort ?? '');
      setSuccess('Loaded referrer tags');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tags');
    } finally {
      setTagBusy(false);
    }
  };

  const saveShareEarnTags = async () => {
    const id = tagUserId.trim();
    if (!id) {
      setError('Enter a user UUID');
      return;
    }
    setError(null);
    setTagBusy(true);
    try {
      await apiService.patchUserShareEarnTags(id, {
        shareEarnBrand: tagBrand.trim() || null,
        shareEarnCohort: tagCohort.trim() || null,
      });
      setSuccess('Referrer tags saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tags');
    } finally {
      setTagBusy(false);
    }
  };

  const runReferredUserDiagnostic = async () => {
    const id = diagReferredUserId.trim();
    if (!id) {
      setError('Enter the referred user UUID for the diagnostic.');
      return;
    }
    setError(null);
    setSuccess(null);
    setDiagBusy(true);
    setDiagResult(null);
    try {
      const data = await apiService.getReferralDiagnostics(id);
      setDiagResult(data);
      setSuccess('Diagnostic loaded');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Diagnostic request failed');
    } finally {
      setDiagBusy(false);
    }
  };

  const handleUpdateReviewStatus = async (
    event: ReferralAbuseEvent,
    status: 'clear' | 'blocked_for_review' | 'blocked'
  ) => {
    const reason = window.prompt('Optional review note', event.reviewReason || event.suspicionReason) || undefined;
    try {
      await apiService.updateReferralReviewStatus(event.id, { status, reason });
      setSuccess('Referral review status updated');
      loadAbuseReview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update referral review status');
    }
  };

  return (
    <div className="finance-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Share & Earn</h1>
          <p className="page-sub">Manage referral campaigns and payout config</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary" onClick={() => setReleaseWizardOpen(true)}>
            Guided release
          </button>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              setCreateOpen(true);
              setFormKey('');
              setFormConfig('{}');
              setFormEnabled(true);
            }}
          >
            New Campaign
          </button>
        </div>
      </div>

      {funnel && (
        <div className="stats">
          <div className="stat">
            <div className="stat-icon cyan">🔗</div>
            <div className="stat-val">{funnel.clicks.toLocaleString()}</div>
            <div className="stat-label">Share link clicks</div>
          </div>
          <div className="stat">
            <div className="stat-icon orange">🧑‍🤝‍🧑</div>
            <div className="stat-val">{funnel.attributions.toLocaleString()}</div>
            <div className="stat-label">Attributions</div>
          </div>
          <div className="stat">
            <div className="stat-icon cyan">✅</div>
            <div className="stat-val">{funnel.conversions.toLocaleString()}</div>
            <div className="stat-label">Conversions (trigger)</div>
          </div>
          <div className="stat">
            <div className="stat-icon orange">💰</div>
            <div className="stat-val">{(funnel.payoutCents / 100).toFixed(0)}</div>
            <div className="stat-label">Credited payouts (major units)</div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 22 }}>
        <div className="card-head">
          <span className="card-title">Referrer tags (campaign layering)</span>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0, marginBottom: 14 }}>
            Sets <code>share_earn_brand</code> and <code>share_earn_cohort</code> on the user for live{' '}
            <code>brand_overrides</code> / <code>cohort_overrides</code> in referral campaign JSON. Does not affect
            frozen policy snapshots on existing share links.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
            <div style={{ flex: '1 1 220px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>User ID</label>
              <input
                className="finance-input"
                value={tagUserId}
                onChange={(e) => setTagUserId(e.target.value)}
                placeholder="UUID"
                autoComplete="off"
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <button type="button" className="btn btn-secondary" disabled={tagBusy} onClick={loadShareEarnTags}>
              Load
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Brand tag</label>
              <input
                className="finance-input"
                value={tagBrand}
                onChange={(e) => setTagBrand(e.target.value)}
                maxLength={64}
                placeholder="e.g. partner"
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Cohort tag</label>
              <input
                className="finance-input"
                value={tagCohort}
                onChange={(e) => setTagCohort(e.target.value)}
                maxLength={64}
                placeholder="e.g. vip"
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <button type="button" className="btn btn-primary" disabled={tagBusy} onClick={saveShareEarnTags}>
              {tagBusy ? '…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 22 }}>
        <div className="card-head">
          <span className="card-title">Referred user payout diagnostic</span>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0, marginBottom: 14 }}>
            Same tool as Support → User Lookup: enter the <strong>referee</strong> user ID to see eligibility, resolved
            amounts, and payout trigger.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: diagResult ? 16 : 0 }}>
            <div style={{ flex: '1 1 280px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Referred user UUID</label>
              <input
                className="finance-input"
                value={diagReferredUserId}
                onChange={(e) => setDiagReferredUserId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                autoComplete="off"
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <button type="button" className="btn btn-secondary" disabled={diagBusy} onClick={runReferredUserDiagnostic}>
              {diagBusy ? 'Loading…' : 'Run diagnostic'}
            </button>
          </div>
          {diagResult && (
            <div
              style={{
                borderTop: '1px solid var(--glass-border)',
                paddingTop: 16,
                fontSize: 14,
              }}
            >
              <p style={{ margin: '0 0 12px 0', lineHeight: 1.5 }}>{diagResult.summary}</p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Eligibility:</strong>{' '}
                {diagResult.eligibilityCheck.eligible ? (
                  <span className="status status-approved">Eligible</span>
                ) : (
                  <span className="status status-rejected">Not eligible</span>
                )}
                {diagResult.eligibilityCheck.reason && (
                  <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{diagResult.eligibilityCheck.reason}</span>
                )}
              </p>
              {diagResult.resolution && (
                <dl className="finance-diag-dl">
                  <dt>Policy</dt>
                  <dd>{diagResult.resolution.policyResolution}</dd>
                  <dt>Click country / platform</dt>
                  <dd>
                    {diagResult.resolution.refereeCountry ?? '—'} / {diagResult.resolution.refereePlatform ?? '—'}
                  </dd>
                  <dt>Referrer tags</dt>
                  <dd>
                    {diagResult.resolution.referrerBrandTag ?? '—'} / {diagResult.resolution.referrerCohortTag ?? '—'}
                  </dd>
                  <dt>Amounts (ref / referee)</dt>
                  <dd>
                    £{(diagResult.resolution.referrerCents / 100).toFixed(2)} / £
                    {(diagResult.resolution.refereeCents / 100).toFixed(2)}
                  </dd>
                  <dt>Trigger / max bets</dt>
                  <dd>
                    {diagResult.resolution.payoutTrigger} · up to {diagResult.resolution.maxBetsCount} ({diagResult.resolution.basis}
                    {diagResult.resolution.basis === 'platform_fee' ? `, ${diagResult.resolution.sharePercent}%` : ''})
                  </dd>
                </dl>
              )}
            </div>
          )}
        </div>
      </div>

      {campaignStats.length > 0 && (
        <div className="card" style={{ marginBottom: 22 }}>
          <div className="card-head">
            <span className="card-title">Campaign performance</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <table className="finance-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Ver.</th>
                  <th>Links</th>
                  <th>Clicks</th>
                  <th>Attr.</th>
                  <th>Payouts</th>
                </tr>
              </thead>
              <tbody>
                {campaignStats.map((row) => (
                  <tr key={row.id}>
                    <td><code>{row.key}</code></td>
                    <td>v{row.version}</td>
                    <td>{row.links}</td>
                    <td>{row.clicks}</td>
                    <td>{row.attributions}</td>
                    <td>{(row.payoutCents / 100).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>Loading...</div>
          ) : campaigns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
              No campaigns. Create one to get started.
            </div>
          ) : (
            <div className="table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Status</th>
                    <th>Payout</th>
                    <th>Published</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.id}>
                      <td><strong>{c.key}</strong></td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span className={`status status-${c.enabled ? 'approved' : 'rejected'}`}>
                            {c.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          {c.isDefault && <span className="status status-pending">Default</span>}
                          <span className="status status-approved">v{c.version}</span>
                        </div>
                      </td>
                      <td>
                        {editingId === c.id ? (
                          <div style={{ maxWidth: 400 }}>
                            <label style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Config (JSON)</label>
                            <textarea
                              value={formConfig}
                              onChange={(e) => setFormConfig(e.target.value)}
                              rows={6}
                              style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
                            />
                            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                              <button className="btn btn-sm btn-primary" onClick={() => handleUpdate(c.id)}>Save</button>
                              <button className="btn btn-sm btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <span style={{ fontSize: 13 }}>
                            {cfg(c).basis === 'platform_fee'
                              ? `${cfg(c).sharePercent ?? 0}% of platform fee`
                              : `£${((cfg(c).referrerCents ?? 0) / 100).toFixed(2)} / £${((cfg(c).refereeCents ?? 0) / 100).toFixed(2)}`}
                            {cfg(c).maxBetsCount != null && ` · max ${cfg(c).maxBetsCount} bets`}
                          </span>
                        )}
                      </td>
                      <td>{c.publishedAt ? new Date(c.publishedAt).toLocaleDateString() : '—'}</td>
                      <td>
                        {editingId !== c.id && (
                          <div className="action-buttons">
                            <button className="btn btn-sm btn-secondary" onClick={() => startEdit(c)}>Edit</button>
                            <button className="btn btn-sm btn-secondary" onClick={() => handleValidate(c.config)}>Validate</button>
                            <button className="btn btn-sm btn-secondary" onClick={() => handlePreview(c.config)}>Preview</button>
                            <button className="btn btn-sm btn-secondary" onClick={() => handleVersions(c.id)}>Versions</button>
                            <button className="btn btn-sm btn-secondary" onClick={() => handleRollback(c.id)}>Rollback</button>
                            {!c.isDefault && (
                              <button className="btn btn-sm btn-secondary" onClick={() => handleSetDefault(c.id)}>Set Default</button>
                            )}
                            {!c.publishedAt && (
                              <button className="btn btn-sm btn-success" onClick={() => handlePublish(c.id)}>Publish</button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-head">
          <span className="card-title">Creative Templates</span>
          <button className="btn btn-secondary" onClick={handleCreateTemplate}>
            New Template
          </button>
        </div>
        <div className="card-body">
          {templates.length === 0 ? (
            <div style={{ color: 'var(--text-muted)' }}>No templates yet.</div>
          ) : (
            <div className="table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Channel</th>
                    <th>Version</th>
                    <th>Published</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => (
                    <tr key={template.id}>
                      <td>{template.key}</td>
                      <td>{template.channel}</td>
                      <td>{template.version}</td>
                      <td>{template.publishedAt ? new Date(template.publishedAt).toLocaleDateString() : '—'}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-secondary" onClick={() => handleEditTemplate(template)}>
                            Edit
                          </button>
                          <button className="btn btn-sm btn-secondary" onClick={() => handleTemplateVersions(template.id)}>
                            Versions
                          </button>
                          <button className="btn btn-sm btn-secondary" onClick={() => handleRollbackTemplate(template.id)}>
                            Rollback
                          </button>
                          {!template.publishedAt && (
                            <button className="btn btn-sm btn-success" onClick={() => handlePublishTemplate(template.id)}>
                              Publish
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-head">
          <span className="card-title">Score Models</span>
          <button className="btn btn-secondary" onClick={handleCreateScoreModel}>New Score Model</button>
        </div>
        <div className="card-body">
          {scoreModels.length === 0 ? <div style={{ color: 'var(--text-muted)' }}>No score models yet.</div> : (
            <div className="table-wrap">
              <table className="finance-table">
                <thead><tr><th>Key</th><th>Version</th><th>Published</th><th>Actions</th></tr></thead>
                <tbody>
                  {scoreModels.map((model) => (
                    <tr key={model.id}>
                      <td>{model.key}</td>
                      <td>{model.version}</td>
                      <td>{model.publishedAt ? new Date(model.publishedAt).toLocaleDateString() : '—'}</td>
                      <td><div className="action-buttons">
                        <button className="btn btn-sm btn-secondary" onClick={() => handleEditScoreModel(model)}>Edit</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleScoreModelVersions(model.id)}>Versions</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleRollbackScoreModel(model.id)}>Rollback</button>
                        {!model.publishedAt && <button className="btn btn-sm btn-success" onClick={() => handlePublishScoreModel(model.id)}>Publish</button>}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-head">
          <span className="card-title">Country Policies</span>
          <button className="btn btn-secondary" onClick={handleCreateCountryPolicy}>New Override</button>
        </div>
        <div className="card-body">
          {countryPolicies.length === 0 ? <div style={{ color: 'var(--text-muted)' }}>No country overrides configured.</div> : (
            <div className="table-wrap">
              <table className="finance-table">
                <thead><tr><th>Country</th><th>Policy</th><th>Expires</th><th>Actions</th></tr></thead>
                <tbody>
                  {countryPolicies.map((policy) => (
                    <tr key={policy.id}>
                      <td>{policy.countryCode}</td>
                      <td>{policy.policyKey}</td>
                      <td>{policy.effectiveTo ? new Date(policy.effectiveTo).toLocaleDateString() : '—'}</td>
                      <td><div className="action-buttons">
                        <button className="btn btn-sm btn-secondary" onClick={() => handleEditCountryPolicy(policy)}>Edit</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleCountryPolicyVersions(policy)}>Versions</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleRollbackCountryPolicy(policy)}>Rollback</button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-head">
          <span className="card-title">Referral Abuse Review</span>
        </div>
        <div className="card-body">
          {abuseEvents.length === 0 ? <div style={{ color: 'var(--text-muted)' }}>No suspicious referrals right now.</div> : (
            <div className="table-wrap">
              <table className="finance-table">
                <thead><tr><th>Referrer</th><th>Referred</th><th>Status</th><th>Reason</th><th>Actions</th></tr></thead>
                <tbody>
                  {abuseEvents.map((event) => (
                    <tr key={event.id}>
                      <td>{event.referrerEmail}</td>
                      <td>{event.referredEmail}</td>
                      <td>{event.reviewStatus ?? 'clear'}</td>
                      <td>{event.reviewReason || event.suspicionReason}</td>
                      <td><div className="action-buttons">
                        <button className="btn btn-sm btn-secondary" onClick={() => handleUpdateReviewStatus(event, 'clear')}>Clear</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleUpdateReviewStatus(event, 'blocked_for_review')}>Hold</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleUpdateReviewStatus(event, 'blocked')}>Block</button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-head">
          <span className="card-title">Referral Payouts</span>
          <button className="btn btn-secondary" onClick={loadPayouts}>Refresh</button>
        </div>
        <div className="card-body">
          {recentPayouts.length === 0 ? (
            <div style={{ color: 'var(--text-muted)' }}>No recent payouts.</div>
          ) : (
            <div className="table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th>Role</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Campaign</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPayouts.map((p) => (
                    <tr key={p.id}>
                      <td>{p.recipientEmail}</td>
                      <td>{p.recipientRole}</td>
                      <td>£{(p.amountCents / 100).toFixed(2)}</td>
                      <td><span className={`status status-${p.status === 'credited' ? 'approved' : p.status === 'reversed' || p.status === 'blocked_for_review' ? 'rejected' : 'pending'}`}>{p.status}</span></td>
                      <td>{p.createdAt ? new Date(p.createdAt).toLocaleString() : '—'}</td>
                      <td>{p.campaignKey ?? '—'}</td>
                      <td>
                        {p.status === 'credited' && (
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-secondary"
                              disabled={payoutActioningId !== null}
                              onClick={() => handleReversePayout(p.id)}
                            >
                              {payoutActioningId === p.id ? '…' : 'Reverse'}
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              disabled={payoutActioningId !== null}
                              onClick={() => handleBlockPayout(p.id)}
                            >
                              {payoutActioningId === p.id ? '…' : 'Block'}
                            </button>
                          </div>
                        )}
                        {p.status !== 'credited' && <span>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <CampaignReleaseWizard
        open={releaseWizardOpen}
        onClose={() => setReleaseWizardOpen(false)}
        campaigns={campaigns}
        onPublished={() => {
          setSuccess('Campaign published');
          loadCampaigns();
        }}
      />

      {createOpen && (
        <div className="modal-overlay" onClick={() => setCreateOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h2>New Campaign</h2>
            </div>
            <div className="modal-body">
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Key (unique)</label>
              <input
                type="text"
                value={formKey}
                onChange={(e) => setFormKey(e.target.value)}
                placeholder="e.g. share_earn_2024"
                style={{ width: '100%', marginBottom: 16, padding: 8 }}
              />
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Config (JSON)</label>
              <textarea
                value={formConfig}
                onChange={(e) => setFormConfig(e.target.value)}
                rows={10}
                placeholder='{"referrer_cents": 500, "referee_cents": 250, "payout": {"max_bets_count": 3}}'
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 12, padding: 8 }}
              />
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Supports: referrer_cents, referee_cents, share_percent, max_bets_count, payout.basis (platform_fee|fixed), payout.share_percent, eligibility, country_overrides
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <input type="checkbox" checked={formEnabled} onChange={(e) => setFormEnabled(e.target.checked)} />
                Enabled
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="button-secondary" onClick={() => setCreateOpen(false)}>Cancel</button>
              <button type="button" className="button-primary" onClick={handleCreate}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface Withdrawal {
  id: string;
  userId: string;
  amount: number;
  currency?: string;
  status: string;
  bankIban: string | null;
  bankHolder: string | null;
  payoutMethod?: string | null;
  payoutEmail?: string | null;
  userEmail: string;
  username: string;
  createdAt: string;
}

interface FinancePageProps {
  view: string;
}

const FinancePage: React.FC<FinancePageProps> = ({ view }) => {
  const { setCurrentPage } = useAdmin();
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | undefined>(undefined);
  const [actionWithdrawal, setActionWithdrawal] = useState<Withdrawal | null>(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [reviewWithdrawal, setReviewWithdrawal] = useState<Withdrawal | null>(null);

  const loadWithdrawals = async (status?: 'pending' | 'approved' | 'rejected' | 'cancelled') => {
    setLoading(true);
    setError(null);
    try {
      const { withdrawals: data } = await apiService.getWithdrawals(status);
      setWithdrawals(data || []);
      if (status === 'pending') {
        setPendingCount((data || []).length);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load withdrawals');
      setWithdrawals([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingCount = async () => {
    try {
      const { withdrawals: data } = await apiService.getWithdrawals('pending');
      setPendingCount((data || []).length);
    } catch {
      setPendingCount(0);
    }
  };

  useEffect(() => {
    if (view === 'finance-dashboard') {
      loadPendingCount();
    } else if (view === 'payout-queue') {
      loadWithdrawals('pending');
    } else if (view === 'withdrawals') {
      loadWithdrawals(statusFilter);
    }
  }, [view, statusFilter]);

  const handleApprove = async () => {
    if (!actionWithdrawal || processing) return;
    setProcessing(true);
    try {
      await apiService.approveWithdrawal(actionWithdrawal.id);
      setSuccess('Withdrawal approved successfully');
      setApproveModalOpen(false);
      setActionWithdrawal(null);
      loadWithdrawals(view === 'payout-queue' ? 'pending' : statusFilter);
      loadPendingCount();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!actionWithdrawal || processing) return;
    setProcessing(true);
    try {
      await apiService.rejectWithdrawal(actionWithdrawal.id, rejectReason || undefined);
      setSuccess('Withdrawal rejected');
      setRejectModalOpen(false);
      setActionWithdrawal(null);
      setRejectReason('');
      loadWithdrawals(view === 'payout-queue' ? 'pending' : statusFilter);
      loadPendingCount();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const formatAmount = (amount: number) => `£${(amount || 0).toFixed(2)}`;
  const formatDate = (d: string) => (d ? new Date(d).toLocaleString() : '—');

  // Finance dashboard
  if (view === 'finance-dashboard') {
    return (
      <div className="finance-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">Finance Dashboard</h1>
            <p className="page-sub">Overview and quick actions</p>
          </div>
        </div>

        <div className="stats">
          <div className="stat clickable" onClick={() => setCurrentPage('payout-queue')}>
            <div className="stat-icon orange">💸</div>
            <div className="stat-val">{pendingCount}</div>
            <div className="stat-label">Pending Withdrawals</div>
          </div>
          <div className="stat clickable" onClick={() => setCurrentPage('withdrawals')}>
            <div className="stat-icon cyan">📋</div>
            <div className="stat-val">—</div>
            <div className="stat-label">All Withdrawals</div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">Recent Activity</span>
            <button className="btn btn-secondary" onClick={() => setCurrentPage('payout-queue')}>
              View Payout Queue
            </button>
          </div>
          <div className="card-body">
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px' }}>
              No recent activity. Use Payout Queue to process withdrawals.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Payout queue & Withdrawals (shared table)
  if (view === 'payout-queue' || view === 'withdrawals') {
    return (
      <div className="finance-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">
              {view === 'payout-queue' ? 'Payout Queue' : 'Withdrawals'}
            </h1>
            <p className="page-sub">
              {view === 'payout-queue'
                ? 'Approve or reject pending withdrawal requests'
                : 'View and filter all withdrawals'}
            </p>
          </div>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success" style={{ marginBottom: 16 }}>
            {success}
          </div>
        )}

        {view === 'withdrawals' && (
          <div className="filter-row">
            <label>Status:</label>
            <select
              value={statusFilter ?? ''}
              onChange={(e) =>
                setStatusFilter(
                  (e.target.value as '' | 'pending' | 'approved' | 'rejected') || undefined
                )
              }
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        )}

        <div className="card">
          <div className="card-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
                Loading...
              </div>
            ) : withdrawals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
                No withdrawals found
              </div>
            ) : (
              <div className="table-wrap">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Amount</th>
                      <th>Bank Details</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map((w) => (
                      <tr
                        key={w.id}
                        className="clickable-row"
                        onClick={() => setReviewWithdrawal(w)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <div className="user-cell">
                            <strong>{w.username || w.userEmail || w.userId}</strong>
                            <span className="muted">{w.userEmail}</span>
                          </div>
                        </td>
                        <td>{formatAmount(w.amount)}</td>
                        <td>
                          <div className="bank-cell">
                            <span>{w.bankHolder || '—'}</span>
                            <span className="muted">{w.bankIban || '—'}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`status status-${w.status}`}>{w.status}</span>
                        </td>
                        <td>{formatDate(w.createdAt)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => setReviewWithdrawal(w)}
                            >
                              Review
                            </button>
                            {view === 'payout-queue' && w.status === 'pending' && (
                              <>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => {
                                    setActionWithdrawal(w);
                                    setApproveModalOpen(true);
                                  }}
                                >
                                  Approve
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => {
                                    setActionWithdrawal(w);
                                    setRejectReason('');
                                    setRejectModalOpen(true);
                                  }}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <ConfirmModal
          isOpen={approveModalOpen}
          title="Approve Withdrawal"
          message={
            actionWithdrawal
              ? `Approve withdrawal of ${formatAmount(actionWithdrawal.amount)} for ${actionWithdrawal.username || actionWithdrawal.userEmail}?`
              : ''
          }
          confirmText="Approve"
          confirmButtonClass="success"
          onConfirm={handleApprove}
          onCancel={() => {
            setApproveModalOpen(false);
            setActionWithdrawal(null);
          }}
        />

        {rejectModalOpen && (
          <div className="modal-overlay" onClick={() => setRejectModalOpen(false)}>
            <div className="modal-content reject-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Reject Withdrawal</h2>
              </div>
              <div className="modal-body">
                {actionWithdrawal && (
                  <p style={{ marginBottom: 16 }}>
                    Reject withdrawal of {formatAmount(actionWithdrawal.amount)} for{' '}
                    {actionWithdrawal.username || actionWithdrawal.userEmail}?
                  </p>
                )}
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                  Reason (optional)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  rows={3}
                  className="reject-reason-input"
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="button-secondary"
                  onClick={() => {
                    setRejectModalOpen(false);
                    setActionWithdrawal(null);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-danger"
                  onClick={handleReject}
                  disabled={processing}
                >
                  {processing ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        )}

        <WithdrawalReviewPanel
          withdrawal={reviewWithdrawal}
          isOpen={reviewWithdrawal !== null}
          onClose={() => setReviewWithdrawal(null)}
          onActionComplete={(action) => {
            setSuccess(action === 'approved' ? 'Withdrawal approved successfully' : 'Withdrawal rejected');
            loadWithdrawals(view === 'payout-queue' ? 'pending' : statusFilter);
            loadPendingCount();
          }}
        />
      </div>
    );
  }

  // Share & Earn campaign management
  if (view === 'share-earn') {
    return (
      <ShareEarnView />
    );
  }

  // Placeholder views: deposits, revenue-report, fee-report, export-reports
  const placeholderViews = [
    'deposits',
    'revenue-report',
    'fee-report',
    'export-reports',
  ];
  if (placeholderViews.includes(view)) {
    const labels: Record<string, string> = {
      deposits: 'Deposits',
      'revenue-report': 'Revenue Report',
      'fee-report': 'Fee Report',
      'export-reports': 'Export Reports',
    };
    return (
      <div className="finance-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">{labels[view] || view}</h1>
            <p className="page-sub">Coming soon</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 48 }}>
              {view === 'deposits'
                ? 'Deposit management is not yet available. No deposit admin API exists.'
                : 'This feature is coming soon.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="finance-page">
      <div className="page-head">
        <h1 className="page-title">Finance</h1>
        <p className="page-sub">Finance management</p>
      </div>
      <div className="card">
        <div className="card-body">
          <p style={{ color: 'var(--text-muted)' }}>Unknown view: {view}</p>
        </div>
      </div>
    </div>
  );
};

export default FinancePage;
